import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Bed, Bath, Car, Home, Search } from 'lucide-react';
import { floorPlans } from '../data/mockData';

const FloorPlans = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('All');

  const styles = ['All', 'Ranch', 'Modern Farmhouse', 'Classic Farmhouse', 'Transitional Craftsman', 'Traditional Two-Story', 'Duplex Investment'];

  const filteredPlans = floorPlans.filter(plan => {
    const matchesSearch = plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          plan.style.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStyle = selectedStyle === 'All' || plan.style === selectedStyle;
    return matchesSearch && matchesStyle;
  });

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-[#262626] mb-6">
              Home Plan Portfolio
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Designs for every vision. Each EC Home Builds plan has been thoughtfully designed to maximize livability, appeal, and long-term value.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search by name or style..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-gray-300 focus:border-[#DAA520] focus:ring-[#DAA520]"
              />
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {styles.map(style => (
                <Button
                  key={style}
                  onClick={() => setSelectedStyle(style)}
                  variant={selectedStyle === style ? 'default' : 'outline'}
                  className={selectedStyle === style 
                    ? 'bg-[#DAA520] hover:bg-[#B8860B] text-white'
                    : 'border-gray-300 text-gray-700 hover:border-[#DAA520] hover:text-[#DAA520]'
                  }
                  size="sm"
                >
                  {style}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Floor Plans Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredPlans.length === 0 ? (
            <div className="text-center py-20">
              <Home className="mx-auto text-gray-400 mb-4" size={64} />
              <h3 className="text-2xl font-bold text-gray-700 mb-2">No plans found</h3>
              <p className="text-gray-500">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPlans.map((plan) => (
                <Card key={plan.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-[#DAA520]">
                  <div className="relative overflow-hidden h-64">
                    <img
                      src={plan.thumbnail}
                      alt={plan.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-[#DAA520] text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                      {plan.sqft} sq. ft.
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                      <Badge className="bg-white/90 text-[#262626] hover:bg-white">
                        {plan.style}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-bold text-[#262626] mb-4">{plan.name}</h3>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center space-x-2 text-gray-700">
                        <Bed className="text-[#DAA520]" size={20} />
                        <span className="text-sm font-medium">{plan.bedrooms} Bed</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-700">
                        <Bath className="text-[#DAA520]" size={20} />
                        <span className="text-sm font-medium">{plan.bathrooms} Bath</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-700">
                        <Car className="text-[#DAA520]" size={20} />
                        <span className="text-sm font-medium">{plan.garage}</span>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-6 line-clamp-3 leading-relaxed">
                      {plan.description}
                    </p>
                    <Link to={`/floor-plans/${plan.id}`}>
                      <Button className="w-full bg-[#262626] hover:bg-[#DAA520] text-white transition-all duration-300">
                        View Details
                        <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#262626] mb-4">
              Portfolio Summary
            </h2>
            <p className="text-xl text-gray-600">
              Compare our complete collection at a glance
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-2 border-gray-200">
              <thead className="bg-[#262626] text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-bold">Plan Name</th>
                  <th className="px-6 py-4 text-left font-bold">Square Feet</th>
                  <th className="px-6 py-4 text-left font-bold">Beds</th>
                  <th className="px-6 py-4 text-left font-bold">Baths</th>
                  <th className="px-6 py-4 text-left font-bold">Garage</th>
                  <th className="px-6 py-4 text-left font-bold">Style</th>
                  <th className="px-6 py-4 text-left font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                {floorPlans.map((plan, index) => (
                  <tr key={plan.id} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-[#DAA520]/5 transition-colors`}>
                    <td className="px-6 py-4 font-semibold text-[#262626]">{plan.name}</td>
                    <td className="px-6 py-4 text-gray-700">{plan.sqft}</td>
                    <td className="px-6 py-4 text-gray-700">{plan.bedrooms}</td>
                    <td className="px-6 py-4 text-gray-700">{plan.bathrooms}</td>
                    <td className="px-6 py-4 text-gray-700">{plan.garage}</td>
                    <td className="px-6 py-4 text-gray-700">{plan.style}</td>
                    <td className="px-6 py-4">
                      <Link to={`/floor-plans/${plan.id}`}>
                        <Button size="sm" className="bg-[#DAA520] hover:bg-[#B8860B] text-white">
                          View
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#DAA520]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Customize Your Plan?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Each plan can be customized to meet your specific needs, lot requirements, and design preferences.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#262626] hover:bg-black text-white px-8 py-6 text-lg">
                Schedule Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/cost-estimate">
              <Button variant="outline" className="bg-white/10 backdrop-blur-md border-white text-white hover:bg-white hover:text-[#262626] px-8 py-6 text-lg">
                Get Cost Estimate
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FloorPlans;
